import type { OptionDB } from "@project/emerge";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import type { CornerType } from "../interfaces/CornerType";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";

function norm(s: string): string {
  return s.replaceAll(/\s/g, "");
}

/**
 * Legacy imports are missing `kind`, keep flat `model`/`rotation`, or point to
 * the removed `stl/` meshes for `angleVariant`. Those shapes cannot be resolved
 * by `CornerBuildStep` the same way a freshly created corner is resolved from
 * the catalog.
 */
function needsCatalogHydration(ps: Record<string, unknown>): boolean {
  if (ps.kind !== LTCaseUserOption.CornerType) return true;
  if (typeof ps.model === "string" && ps.model.length > 0) return true;

  const meshes = ps.meshes as Record<string, unknown> | undefined;
  const angleVariantMesh = meshes?.angleVariant as { url?: string } | undefined;
  if (angleVariantMesh?.url?.startsWith("stl/")) return true;

  const angleVariantInfo = ps.angleVariant as { code?: string } | undefined;
  if (angleVariantInfo && !meshes?.angleVariant) return true;

  return false;
}

function findCornerInCatalog(
  legacyName: string | undefined,
  all: CornerType[],
): CornerType | undefined {
  if (!legacyName) return undefined;
  const key = norm(legacyName);
  for (const candidate of all) {
    if (candidate.name && norm(candidate.name) === key) return candidate;
    if (candidate.aliases?.some((a) => norm(a) === key)) return candidate;
  }
  return undefined;
}

function cloneOption<T>(value: T): T {
  return structuredClone(value);
}

function hydrateCornerPartSelection(
  ps: Record<string, unknown>,
  optionDb: OptionDB,
): CornerType | undefined {
  if (!needsCatalogHydration(ps)) return undefined;

  const legacyName = typeof ps.name === "string" ? ps.name : undefined;
  const all =
    optionDb.getAllOfKind<CornerType>(LTCaseUserOption.CornerType) ?? [];
  const catalog = findCornerInCatalog(legacyName, all);
  if (!catalog) return undefined;

  const clone = cloneOption(catalog);
  const forceOrientation = (ps as { forceOrientation?: boolean })
    .forceOrientation;
  const noBracketOffset = (ps as { noBracketOffset?: boolean }).noBracketOffset;
  if (forceOrientation !== undefined) clone.forceOrientation = forceOrientation;
  if (noBracketOffset !== undefined) clone.noBracketOffset = noBracketOffset;
  return clone;
}

/**
 * After `migrateFlightCaseSettings`, replace imported corner blobs (missing
 * `kind`, flat `model`/`rotation`, or legacy `stl/` `angleVariant` meshes) with
 * the matching OptionDB {@link CornerType} row.
 *
 * Legacy data points `angleVariant` at `stl/AH4114*.dae`, which the mesh loader
 * applies a different Z-UP→Y-UP normalization to than the catalog's
 * `sunlabs/AH4114*` asset. The result is a wrongly rotated corner when a case
 * saved with the old shape is reopened.
 */
export function hydrateImportedCornerPartSelections(
  settings: FlightCaseSettings,
  optionDb: OptionDB,
): void {
  const ext = settings.exteriorOptions as unknown as
    | Record<string, unknown>
    | undefined;
  if (!ext) return;

  for (const key of ["cornersTop", "cornersBottom"] as const) {
    const ps = ext[key];
    if (!ps || typeof ps !== "object") continue;
    const hydrated = hydrateCornerPartSelection(
      ps as Record<string, unknown>,
      optionDb,
    );
    if (hydrated) ext[key] = hydrated;
  }
}
